import { Alert, Button, List, ListItem } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useNotification } from '../../../hooks/NotificationContext';
import { getConfigurations } from '../../../modules/config';
import {
  createTestCycleWithPlan,
  generateTestPlan,
} from '../../../modules/intelligence';
import { getCurrentProject } from '../../../modules/project';
import Modal from '../../elements/Modal';
import TestCaseSelectionTable from './TestCaseSelectionTable';
import CreateTestCycleForm from '../test-cycles/CreateTestCycleForm';
import TestPlanFilters from './TestPlanFilters';
import { checkRequiredFieldsInCycle } from '../../../lib/executionUtils';
import useExecutionScope from '../../../hooks/useExecutionScope';
import { useTestCycleConfig } from '../../../query/apiQueries';
import {
  useProjectUsers,
  useJenkinsJobs
} from '../../../query/apiQueries';
const GenerateTestPlanModal = ({ open, handleClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedTestCases, setSelectedTestCases] = useState([]);
  const [selectedModules, setSelectedModules] = useState([]);
  const [selectedPriority, setSelectedPriority] = useState([]);
  const [moduleOptions, setModuleOptions] = useState([]);
  const [testcases, setTestCases] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [responseMessage, setResponseMessage] = useState(null);
  const [currentProject, setCurrentProject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: '',
    purpose: '',
    type: '',
    releaseVersion: '',
    owner: '',
    job: '',
    platforms: [],
    subPlatforms: [],
    browsers: [],
    geographies: [],
  });

  const { projectId } = useParams();
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { isScopeDefined,isPlatformsDefined } = useExecutionScope(projectId);
  const { data: config = [], isLoading: loadingConfig } = useTestCycleConfig(projectId);
  const { data: projectUsers = [], isLoading: loadingUsers } = useProjectUsers(projectId);
   const { data: jobs = [] } = useJenkinsJobs(projectId);

  const fetchData = async () => {
    try {
      const projectResponse = await getCurrentProject(projectId);
      const modulesResponse = await getConfigurations('modules', projectId);
      setCurrentProject(projectResponse);
      setModuleOptions(modulesResponse.data);
    } catch (error) {
      console.log(error);
    }
  };

  const handleContinue = () => {
    setCurrentStep(currentStep + 1);
  };

  const handleGoBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleGeneratePlan = async () => {
    setCurrentStep(3);
    setGenerating(true);
    await fetchTestCases();
    setGenerating(false);
  };

  const fetchTestCases = async () => {
    try {
      const response = await generateTestPlan({
        moduleIds: selectedModules,
        priorities: selectedPriority,
        gainssProjectId: projectId,
      });
      setTestCases(response);
    } catch (error) {
      console.log(error);
    }
  };

  const handleCloseModal = () => {
    setCurrentStep(1);
    setSelectedTestCases([]);
    setSelectedModules([]);
    setSelectedPriority([]);
    setResponseMessage(null);
    setFormData({
      name: '',
      description: '',
      status: '',
      purpose: '',
      type: '',
      releaseVersion: '',
      owner: '',
      job: '',
      platforms: [],
      subPlatforms: [],
      browsers: [],
      geographies: [],
    });
    handleClose();
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreateTestCycle = async () => {
    try {
      if (isPlatformsDefined) {
        const errorMessage = checkRequiredFieldsInCycle(formData);
        if (errorMessage) {
          setResponseMessage(errorMessage);
          return;
        }
      }
      const testcycle = {
        ...formData,
        name: formData.name,
        purpose: formData.purpose,
        type: formData.type,
        testcases: selectedTestCases.map((testCase) => testCase.id),
        platforms: formData.platforms,
        subPlatforms: formData.subPlatforms,
        browsers: formData.browsers,
        geographies: formData.geographies,
      };
      const response = await createTestCycleWithPlan({ testcycle }, projectId);
      if (response && response?.success === false) {
        setResponseMessage(response?.message);
        return;
      }
      addNotification(
        'success',
        'Test cycle created successfully! Redirecting to test case associations...'
      );
      handleCloseModal();
      navigate(
        `/project/${projectId}/testcycles/${response?.test_cycle?.key}?type=testcases`
      );
    } catch (error) {
      const errorMessage =
        error?.message || 'Failed to create test cycle. Please try again.';
      addNotification('danger', errorMessage);
    }
  };

  return (
    <Modal
      open={open}
      width="lg"
      handleClose={handleCloseModal}
      modalTitle={
        currentStep === 4 ? 'Create Test Cycle' : 'Generate Test Plan'
      }
      action={() => (
        <>
          {(currentStep === 3 || currentStep === 4) && (
            <Button variant="outlined" color="secondary" onClick={handleGoBack}>
              Back
            </Button>
          )}
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleCloseModal}>
            Cancel
          </Button>

          {currentStep === 2 && (
            <Button
              variant="contained"
              onClick={handleGeneratePlan}
              disabled={
                selectedModules.length === 0 || selectedPriority.length === 0
              }>
              Generate Plan
            </Button>
          )}

          {currentStep === 4 && (
            <>
              <Button
                variant="contained"
                onClick={handleCreateTestCycle}
                disabled={
                  formData.name === '' ||
                  formData.purpose === '' ||
                  formData.type === ''
                }>
                Create
              </Button>
            </>
          )}
          {currentStep === 3 && (
            <>
              <Button
                variant="contained"
                onClick={handleContinue}
                title="Select test cases to perform this action"
                disabled={selectedTestCases.length === 0}>
                Create Test Cycle
              </Button>
            </>
          )}
          {currentStep === 1 && (
            <Button variant="contained" onClick={handleContinue}>
              Continue
            </Button>
          )}
        </>
      )}>
      <>
        {currentStep === 1 && (
          <>
            <p>
              The <strong>Suggested Test Plan</strong> feature in GAINSS
              eliminates the guesswork in test case selection by leveraging
              data-driven insights. Powered by advanced ML algorithms, it
              provides a tailored list of test cases designed to ensure
              stability, reduce effort, and accelerate time to market.
            </p>
            <h5>How to Use the Suggested Test Plan Feature:</h5>
            <ol>
              <li>
                <strong>Select Modules:</strong> Choose the specific modules
                where functionality changes will be implemented in the upcoming
                release.
              </li>
              <li>
                <strong>Set Defect Priorities:</strong> Define the defect
                priority levels to focus on high-impact test cases.
              </li>
            </ol>
            <p className="mb-0">The platform then analyzes the following:</p>
            <ul>
              <li>Historical test execution and defect trends.</li>
              <li>Upcoming user stories and their developers.</li>
              <li>Automation coverage and past performance.</li>
            </ul>
            <p>
              Based on this analysis, GAINSS generates a{' '}
              <strong>targeted list of test cases</strong> — your “first pass” —
              optimized for either automation or manual execution.
            </p>
            <p>
              <strong>Ready to experience intelligent test planning?</strong>{' '}
              Click “Continue” and see how GAINSS transforms your testing
              strategy.
            </p>
          </>
        )}

        {currentStep === 2 && (
          <TestPlanFilters
            moduleOptions={moduleOptions}
            currentProject={currentProject}
            selectedModules={selectedModules}
            setSelectedModules={setSelectedModules}
            selectedPriority={selectedPriority}
            setSelectedPriority={setSelectedPriority}
          />
        )}

        {currentStep === 3 && (
          <TestCaseSelectionTable
            setSelectedTestCases={setSelectedTestCases}
            data={testcases}
            loading={generating}
            selectedTestCases={selectedTestCases}
            moduleOptions={moduleOptions}
            fetchTestCases={fetchTestCases}
          />
        )}

        {currentStep === 4 && (
          <>
            <CreateTestCycleForm
              formData={formData}
              setFormData={setFormData}
              isEditing={true}
              setResponseMessage={setResponseMessage}
              jobs={jobs}
              statuses={config?.status || []}
              purposeOptions={config?.purpose || []}
              typeOptions={config?.type || []}
              projectUsers={projectUsers || loadingUsers}
              isLoading={loadingConfig || loadingUsers}
              isScopeDefined={isScopeDefined}
            />
            <Row className="mb-3 mt-3">
              <Col>
                <Form.Label>Review Selected Test Cases:</Form.Label>
                {selectedTestCases.length > 0 && (
                  <div
                    className="mb-3"
                    style={{
                      maxHeight: '200px',
                      overflowY: 'auto',
                      border: '1px solid #ddd',
                      padding: '10px',
                    }}>
                    <List disablePadding dense={true}>
                      {selectedTestCases.map((testCase) => (
                        <ListItem key={testCase.id}>
                          <strong>{testCase.key}</strong>:{' '}
                          {testCase.summary.slice(0, 120)}
                          {testCase.summary.length > 120 && '...'}
                        </ListItem>
                      ))}
                    </List>
                  </div>
                )}
              </Col>
            </Row>
            {responseMessage && (
              <Alert severity="error" className="mt-2">
                {responseMessage}
              </Alert>
            )}
          </>
        )}

        {currentStep === 5 && (
          <p>Test cycle has been successfully created! Redirecting...</p>
        )}
      </>
    </Modal>
  );
};

export default GenerateTestPlanModal;
